import React, { useEffect, useState, useRef, forwardRef, useMemo } from "react";
import ReactDatePicker from "react-datepicker";
import { format, isValid, parse } from "date-fns";
import { InputText } from "./Input";
import { Alert } from "../../Alert";
import { useToast } from "@chakra-ui/core";
import lodash from 'lodash';
import { errorMessages } from "../../../ErrorConfig/errorConfig";
import { isEmpty } from "lodash";

const isNumber = (num) => {
  return !isNaN(Number(num));
};

const ExampleCustomInput = forwardRef(({ dataTestId, value, onClick, label, name, full, setDate, errorValue, setError, isDisabled, disabled, invalidAlert, placeholderText }, ref) => {
  const [inputText, setInputText] = useState("");
  useEffect(() => {
    if(value && value.length === 10 && isValid(new Date(value))){
      setInputText(value);
    }
    if (isEmpty(value)) {
      setInputText('');
    }
  }, [value]);

  useEffect(()=>{
    if (inputText && inputText.length === 10 && isValid(new Date(inputText))) {
      setDate(new Date(inputText))
    }
  },[inputText])

  useEffect(()=>{
    if (disabled) {
      setInputText('')
    }
  },[disabled])


  const inputTextChangeHandler = (e) => {

    const newInput = lodash.get(e, ["target", "value"], "");

    if (newInput.length < inputText.length) {
      setInputText(newInput);
      setDate(newInput);
      return;
    }

    if (newInput) {
      const simpleIndex = [1,4,7,8,9,10];
      if(simpleIndex.includes(newInput.length) && isNumber(newInput[newInput.length-1])){
        setInputText(newInput);
      }else if(newInput.length === 2 && isNumber(newInput.slice(0, 2))){
          setInputText(newInput + "/");
      }else if(newInput.length === 5 && isNumber(newInput[4])){
        setInputText(newInput + "/");
      }
    } else {
      setInputText("");
    }
  };

  return (
    <div onClick={onClick}>
      <InputText
          ref={ref}
          data-testid={dataTestId}
          label={label}
          name={name}
          errorValue={errorValue}
          iconName="calendar"
          type="input"
          full={full}
          style={{ paddingRight: '3rem' }}
          onBlur={()=>{
            if (inputText && inputText.length === 10 && isValid(new Date(inputText))){
              setDate(new Date(inputText))
            }else if(inputText.length > 0){
              setDate(inputText)
              setInputText('')
              invalidAlert();
            }
          }}
          onPaste={(e) => {
            let pastedText = e.clipboardData.getData('Text');
            pastedText = pastedText.replace(/\//g,'');
            if(isValid(new Date(pastedText))){
                if(pastedText.includes('-')){
                  pastedText = parse(pastedText, "yyyy-MM-dd", new Date())
                }
                setInputText(format(new Date(new Date(pastedText)), "MM/dd/yyyy"))
            }
            if(pastedText.length === 8 && isNumber(pastedText)) {
              pastedText = pastedText.substring(0,2) + '/' + pastedText.substring(2,4) + '/' + pastedText.substring(4, pastedText.length)
              setInputText(pastedText)
            } else {
              setInputText('');
              setError('format');
              invalidAlert();
            }

          }}
          value={inputText}
          onChange={inputTextChangeHandler}
          isDisabled={isDisabled || disabled}
          placeholder={placeholderText}
        />
    </div>
  );
});

export const DatePicker = ({
  label,
  name,
  errorValue,
  initialValue,
  onChange,
  maxDate,
  full,
  skipErrors,
  setFieldError,
  ...props
}) => {
  const [startDate, setStartDate] = useState("");
  let isDateValid = null;
  let wasDateValidated = false;
  const closeRef = useRef();
  const toast = useToast();
  const [formatError, setFormatError] = useState('');

  const error = useMemo(() => formatError || errorValue, [formatError, errorValue]);

  const setError = (error) => {
    if (setFieldError) {
      return setFieldError(name, error);
    }
    setFormatError(error);
  };

  const setDate = (date) => {
    if(isValid(date)){
      const configureDate = (dateToSet, formattedDate) => {
        isDateValid = true;
        setStartDate(dateToSet);
        onChange(formattedDate);
        setError('');
      }
      if (skipErrors && date.length === 0) {
        configureDate('', null);
        return
      }
      let [month, day, year] = [date.getMonth() + 1, date.getDate(), date.getFullYear()]
      if (month < 10) month = "0" + month;
      if (day < 10) day = "0" + day;
      const formatted = `${month}/${day}/${year}`;
      configureDate(date, formatted);
    }else {
      onChange('');
      date !== '' && setError('format');
    }
  };

  const onDateChange = (date) => {
    if (wasDateValidated) {
      wasDateValidated = false;
      return;
    }
    setDate(date);
    isDateValid = true;
  };

  const showAlert = () => {
    if (isDateValid === false && !skipErrors) {
      setStartDate('');
      onChange('');
      if (!closeRef.current) return Alert(toast, errorMessages.canNotContinue, errorMessages.informationMissing, "error", null, closeRef);
    }
  };

  const invalidAlert = () => {
    if (!closeRef.current) return Alert(toast, errorMessages.canNotContinue, errorMessages.informationMissing, "error", null, closeRef);
  }

  useEffect(() => {
    if (initialValue) setStartDate(new Date(initialValue + ', 00:00:00'));
    else if (startDate) setStartDate("");
  }, [initialValue]);

  return (
    <ReactDatePicker
      {...props}
      peekNextMonth
      dateFormat="MM/dd/yyyy"
      placeholderText={props.placeholderText || "MM/DD/YYYY"}
      dropdownMode="select"
      selected={startDate}
      onChange={onDateChange}
      onBlur={showAlert}
      maxDate={maxDate}
      showMonthDropdown
      showYearDropdown
      popperPlacement="bottom"
      popperModifiers={{
          flip: {
              behavior: ["bottom"] 
          },
          preventOverflow: {
              enabled: false 
          },
          hide: {
              enabled: false
          }
      }}
      customInput={
        <ExampleCustomInput
          label={label} 
          name={name}
          dataTestId={props['data-testid']}
          errorValue={error}
          full={full}
          setError={setError}
          isDisabled={props.isDisabled}
          disabled={props.disabled}
          setDate={(date) => {
            isDateValid = true
            setDate(date)
          }}
          placeholderText={props.placeholderText || "MM/DD/YYYY"}
          invalidAlert={invalidAlert}
        />
      }
    />
  );
};